import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

import { getPayments } from "common/services/payments.service";
import TableContainer from "../../Components/Common/TableContainer";

import { getSortObj } from "common/lib/tableDataOptions";

import { isPaymentActive } from "common/lib/paymentOptions";

import Permission from "Components/Common/Permission";

import { useProfile } from "Components/Hooks/UserHooks";
import { normalize } from "common/lib/normalize";
import { isAllowed } from "common/lib/permissionUtils";
import { toast } from "react-toastify";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType?: string;
  agency?: string;
  dateRange?: DateRange;
  region?: string;
  mda?: string;
  paymentCategory?: string;
  thematicArea?: string;
  sector?: string;
  status?: string | null;
}

// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  return <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{value}</div>;
};

const PaymentsModal = ({
  isOpen,
  toggle,
  title,
  filter,
}: {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  filter: any;
}) => {
  const [params] = useState({
    page: 1, // Starts from 1
    pageSize: 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => {
    return [{ id: "createdAt", desc: true }];
  });

  const [searchValue] = useState("");

  const [filterValue, setFilterValue] = useState<FilterValue>(() => {
    return {
      agency: filter?.agency || null,
      status: filter?.status || "all",
      region: filter?.region || null,
      mda: filter?.mda || null,
      positionType: filter?.positionType || null,
      paymentCategory: filter?.paymentCategory || null,
      thematicArea: filter?.thematicArea || null,
      sector: filter?.sector || null,
      dateRange: filter?.dateRange || null,
    };
  });

  useEffect(() => {
    setFilterValue({
      agency: filter?.agency || null,
      status: filter?.status || "all",
      region: filter?.region || null,
      mda: filter?.mda || null,
      positionType: filter?.positionType || null,
      paymentCategory: filter?.paymentCategory || null,
      thematicArea: filter?.thematicArea || null,
      sector: filter?.sector || null,
      dateRange: filter?.dateRange || null,
    });
  }, [filter]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency?.value) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency.value,
            },
          },
        });
      }
      if (filterValue?.region?.value) {
        andConditions.push({
          mda: {
            region: {
              id: {
                $eq: filterValue.region.value,
              },
            },
          },
        });
      }
      if (filterValue?.mda?.value) {
        andConditions.push({
          mda: {
            id: {
              $eq: filterValue.mda.value,
            },
          },
        });
      }
      if (filterValue?.paymentCategory?.value) {
        andConditions.push({
          paymentCategory: {
            id: {
              $eq: filterValue.paymentCategory.value,
            },
          },
        });
      }
      if (filterValue?.thematicArea?.value) {
        andConditions.push({
          thematicArea: {
            id: {
              $eq: filterValue.thematicArea.value,
            },
          },
        });
      }
      if (filterValue?.sector?.value) {
        andConditions.push({
          sector: {
            id: {
              $eq: filterValue.sector.value,
            },
          },
        });
      }
      if (filterValue?.positionType?.value) {
        andConditions.push({
          positionType: {
            id: {
              $eq: filterValue.positionType.value,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate =
          filterValue.dateRange.fromDate instanceof Date
            ? filterValue.dateRange.fromDate
            : new Date(filterValue.dateRange.fromDate);

        const toDate =
          filterValue.dateRange.toDate instanceof Date
            ? filterValue.dateRange.toDate
            : new Date(filterValue.dateRange.toDate);

        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  fromDate.toLocaleDateString("en-CA"),
                  toDate.toLocaleDateString("en-CA"),
                ],
              },
            },
            {
              endDate: {
                $between: [
                  fromDate.toLocaleDateString("en-CA"),
                  toDate.toLocaleDateString("en-CA"),
                ],
              },
            },
          ],
        });
      }

      if (filterValue?.status) {
        if (filterValue.status === "active")
          andConditions.push({
            startDate: {
              $lte: new Date().toISOString(),
            },
            endDate: {
              $gte: new Date().toISOString(),
            },
          });
        else if (filterValue.status === "inactive")
          andConditions.push({
            $or: [
              {
                startDate: {
                  $gt: new Date().toISOString(),
                },
              },
              {
                endDate: {
                  $lt: new Date().toISOString(),
                },
              },
            ],
          });
      }

      andConditions.push({
        $or: [
          {
            individual: {
              fullName: {
                $containsi: searchValue,
              },
            },
          },
          {
            positionTitle: {
              $containsi: searchValue,
            },
          },
          {
            mda: {
              name: {
                $containsi: searchValue,
              },
            },
          },
          {
            mda: {
              region: {
                name: {
                  $containsi: searchValue,
                },
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "paymentCategory",
            "thematicArea",
            "sector",
          ],
          filters: {
            $and: andConditions,
          },
        };
        const response: any = await getPayments(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "payment",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "mda.name",
        id: "mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-bank-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5
                    className="fs-14 mb-1"
                    // style={{
                    //   whiteSpace: "nowrap",
                    //   overflow: "hidden",
                    //   textOverflow: "ellipsis",
                    // }}
                  >
                    <Permission
                      alternative={
                        <Link
                          to={`/payments/${rowData?.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/payments/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.mda?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Position Title",
        accessorKey: "positionTitle",
        enableColumnFilter: false,
        id: "positionTitle",
        cell: (cell: any) => (
          <TextWrapper value={cell.getValue() || "Not Provided"} />
        ),
      },

      {
        header: "Position Type",
        accessorKey: "positionType.name",
        enableColumnFilter: false,
        id: "positionType.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Payment Category",
        accessorKey: "paymentCategory.name",
        enableColumnFilter: false,
        id: "paymentCategory.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = isPaymentActive(
            cell.row.original.startDate || "",
            cell.row.original.endDate || ""
          );
          return (
            <React.Fragment>
              {status === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  Not Provided
                </span>
              ) : status ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Active
                </span>
              ) : status === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Not Active
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },
      {
        header: "Amount / Month",
        accessorKey: "amountPerMonth",
        enableColumnFilter: false,
        id: "amountPerMonth",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },

      //   {
      //     header: "Action",
      //     enableSorting: false,
      //     cell: (cellProps: any) => {
      //       const rowData = cellProps.row.original;
      //       return (
      //         <UncontrolledDropdown>
      //           <DropdownToggle
      //             href="#"
      //             className="btn btn-soft-secondary btn-sm dropdown"
      //             tag="button"
      //           >
      //             <i className="ri-more-fill align-middle"></i>
      //           </DropdownToggle>
      //           <DropdownMenu className="dropdown-menu-end">
      //             <Permission resource="payment" action="findOne">
      //               <DropdownItem href={`/payments/${rowData.id}`}>
      //                 <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
      //                 View
      //               </DropdownItem>
      //             </Permission>

      //             <Permission resource="payment" action="update">
      //               <DropdownItem
      //                 href="/#"
      //                 onClick={(e) => {
      //                   e.preventDefault();
      //                   showUpdateModalForm({
      //                     id: rowData.id,
      //                     ...rowData,
      //                   });
      //                 }}
      //               >
      //                 <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
      //                 Edit
      //               </DropdownItem>
      //             </Permission>

      //             <Permission resource="payment" action="delete">
      //               <DropdownItem divider />
      //               <DropdownItem
      //                 href="#"
      //                 onClick={() => {
      //                   onClickDelete(rowData);
      //                 }}
      //               >
      //                 <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
      //                 Delete
      //               </DropdownItem>
      //             </Permission>
      //           </DropdownMenu>
      //         </UncontrolledDropdown>
      //       );
      //     },
      //   },
    ],
    []
  );

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        style={{ maxWidth: "95%", width: "95%", margin: "20px auto" }}
      >
        <ModalHeader toggle={toggle}>{title}</ModalHeader>
        <ModalBody>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">Payments</h5>
                  <p className="mb-0 text-muted">
                    A list of payments and contracts made to ministries,
                    departments and agencies.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <div className="form-check form-switch form-switch-lg d-flex px-0 form-check-right align-items-center">
                      <Label
                        className="form-check-label me-3"
                        htmlFor="flexSwitchCheckCheckedDisabled2"
                        style={{ width: "120px", marginTop: "5px" }}
                      >
                        Active Payments
                      </Label>{" "}
                      <Input
                        name="isActive"
                        className="form-check-input me-2"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckCheckedDisabled2"
                        checked={filterValue.status === "active" ? true : false}
                        onChange={(e: any) => {
                          setFilterValue({
                            ...filterValue,
                            status: e.target.checked ? "active" : null,
                          });
                          setPage(1);
                        }}
                      />
                    </div>
                    {/* <Permissions
                      checks={[{ resource: "payment", action: "delete" }]}
                    >
                      {selectedCheckBox.length > 0 && (
                        <UncontrolledButtonDropdown>
                          <DropdownToggle
                            color="secondary"
                            caret
                            className="material-shadow-none btn-light"
                          >
                            Bulk Actions - {selectedCheckBox.length}
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem
                              onClick={() => {
                                setDeleteMultipleModal(true);
                              }}
                            >
                              <i className="ri-delete-bin-line align-middle me-2"></i>{" "}
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledButtonDropdown>
                      )}
                    </Permissions>
                    <Permission resource="payment" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          showAddModalForm();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Payment
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        {
                          resource: "payment",
                          action: "import",
                        },
                        {
                          resource: "payment",
                          action: "exportToCsv",
                        },
                      ]}
                    >
                      <Import apiParams={apiParams} />
                    </Permissions> */}
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap "
              />
            </CardBody>
          </Card>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PaymentsModal;
